<template>
  <b-form-group
    :id="id + '-group'"
    :label="label"
    :label-for="id"
    :state="!errors.has(id)"
    :invalid-feedback="errors.first(id)"
    class="form-selectbox"
  >
    <multiselect
      ref="formSelectbox"
      v-model="model"
      :class="{ 'is-invalid': errors.has(id), 'multiselect__content--countrySelect': countryComponent }"
      :name="'select-' + id"
      :options="options"
      :searchable="searchable"
      :show-labels="false"
      :placeholder="placeholder"
      :disabled="disabled"
      :data-testing="dataTesting"
      v-bind="tabIndex"
      track-by="value"
      label="label"
      @input="onChangeModel"
    >
      <span
        slot="noResult"
        v-text="$t('label.no_result')"
      />

      <span
        slot="noOptions"
        v-text="$t('label.no_entry')"
      />
    </multiselect>
    <input
      :id="id"
      ref="formSelectboxHidden"
      v-validate="{ rules: validationRules }"
      :name="id"
      :value="hiddenValue"
      type="hidden"
      data-vv-validate-on="change"
    >
  </b-form-group>
</template>

<script>
import Multiselect from 'vue-multiselect';
import { BFormGroup } from 'bootstrap-vue';

/**
* Form Selectbox with Validation
*/
export default {
  name: 'FormSelectbox',
  components: {
    Multiselect,
    BFormGroup
  },
  inject: {
    $validator: '$validator'
  },
  props: {
    /**
    * selectbox id - it's used also as name attribute for the hidden input field and for error handling
    */
    id: {
      type: String,
      default: ''
    },
    /**
    * label for the bootstrap form group
    */
    label: {
      type: String,
      default: ''
    },
    /**
    * placeholder for multiselect
    */
    placeholder: {
      type: String,
      default: ''
    },
    /**
    * required attribute
    */
    required: {
      type: Boolean,
      default: false
    },
    /**
    * value to fill in data from the api
    */
    value: {
      type: [String, Number],
      default: null
    },
    /**
     * Options to set by the api
     */
    options: {
      type: Array,
      default: () => [],
      required: true
    },
    /**
     * disable selectbox
    */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * enable search functionality of vue-multiselect
    */
    searchable: {
      type: Boolean,
      default: false
    },
    /**
     * Variable for cypress testing
     */
    dataTesting: {
      type: String,
      default: null
    },

    countryComponent: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      /**
      * @model
      */
      model: []
    };
  },
  computed: {
    validationRules() {
      return {
        required: this.required
      };
    },
    hiddenValue(){
      return (this.model) ? this.model.value : '';
    },
    /**
     *  Overwrites the tab index for multiselect components
     *  Is necessary to make multiselects tabbable
     */
    tabIndex(){
      return {
        tabIndex: 0
      };
    }
  },
  watch: {
    // detect changes in store step data
    value(newVal) {
      this.createModel(newVal);
    }
  },
  mounted() {
    const vm = this;
    this.$nextTick(() => vm.createModel(this.value));
  },
  methods: {
    /**
    * update model and validate hidden field - this is a special case for vue-multiselect
    * @event onChangeModel
    * @type {Input}
    */
    onChangeModel(value) {
      this.model = value ? value : this.options[0];
      this.$emit('onchange', { key: this.id, val: this.model });

      if (this.required) {
        this.$validator.validate(
          this.id,
          this.model ? this.model : null
        );
      }
    },
    /**
    * create model from current value
    * @event createModel
    * @param {number} val - current value
    */
    createModel(val) {
      this.model = typeof this.options[0] === 'object' ? this.options.find((o) => o.value === val) : [];
    },
    clear() {
      this.model = this.options[0];
    }
  }
};
</script>

<style lang="scss">
  @import '~vue-multiselect/dist/vue-multiselect.min.css';
  @import './../../styles/multiselect';

  .multiselect__content--countrySelect {
    .multiselect__element:nth-child(-n + 3) {
      span {
        background-color: $color-blue-200 !important;
      }
    }

    .multiselect__element:nth-child(3) {
      border-bottom: 3px solid black;
    }
  }
 
</style>
